import { useEffect, useRef, useState } from 'react'

// Loaded once and shared across every component that calls useRecaptcha —
// `render=explicit` tells api.js not to auto-scan the page for `.g-recaptcha`
// elements (that scan only ever runs once, when the script itself first
// loads), and `onload=` names a global callback api.js calls once it's
// actually ready to accept .render() calls.
let scriptPromise = null
function loadRecaptchaScript() {
  if (window.grecaptcha?.render) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve) => {
    window.__onRecaptchaLoad = resolve
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=__onRecaptchaLoad'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })
  return scriptPromise
}

// Explicitly renders a fresh widget into the returned ref's element every
// time the calling component mounts. This app is a single-page app (React
// Router, no full reload between pages) with more than one page that has its
// own captcha (the homepage's Contact section, and each /websites/:slug
// page) — the CSS-class "implicit" render mode Google's docs default to only
// initializes whichever `.g-recaptcha` divs are already in the DOM the one
// time api.js's script first executes, so a page reached afterward via
// client-side navigation never got a working widget (it only ever "worked"
// after a hard refresh, since that's the only time the div predates the
// script). Explicit rendering, redone on every mount, fixes that for both.
// `enabled` defers fetching Google's recaptcha script (and the widget DOM
// work it does on render) until the caller says it's actually needed — both
// call sites gate this on the form scrolling near the viewport, since it
// sits below the fold and was previously loading unconditionally on mount,
// adding third-party JS/layout work to every page load whether or not the
// visitor ever reaches the form.
export function useRecaptcha(siteKey, enabled = true) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    widgetIdRef.current = null
    loadRecaptchaScript().then(() => {
      if (cancelled || !containerRef.current) return
      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'dark',
      })
      setLoaded(true)
    })
    return () => { cancelled = true }
  }, [siteKey, enabled])

  function getResponse() {
    if (widgetIdRef.current === null) return ''
    return window.grecaptcha?.getResponse(widgetIdRef.current) || ''
  }

  function reset() {
    if (widgetIdRef.current !== null) window.grecaptcha?.reset(widgetIdRef.current)
  }

  return { containerRef, loaded, getResponse, reset }
}
